import { useContext, useState, JSX } from 'react';
import { Prompt, useHistory } from 'react-router-dom';
import * as Survey from 'survey-core';
import { ActiveFacilityContext } from 'context/activeFacility';
import { UserContext } from 'context/user';
import { Roles } from 'constants/roles';
import { AppliedWhen, InstrumentTypes } from 'constants/assessment_types';

import { AssessmentResult } from 'api/assessments/types';
import AssessmentProxy from 'api/assessments/assessmentProxy';
import FlexContainer from 'global_elements/Layouts/FlexContainer';
import { DisplayVariant, AlignVariant, JustifyVariant } from 'global_elements/Layouts/FlexContainer/variants';
import PageLayout from 'global_elements/Layouts/PageLayout';
import { PageLayoutVariant } from 'global_elements/Layouts/PageLayout/variants';
import InlineText from 'global_elements/Text/InlineText';
import { FontColors, FontSizes } from 'global_elements/Text/variants';
import { useTranslation } from 'react-i18next';
import Button from 'global_elements/Button';
import { ButtonVariant } from 'global_elements/Button/variants';
import { hasPixAccess } from 'global_elements/Navigation/lib/navOptions';
import { AssessmentSurvey } from '../assessments/assessment';
import { assessmentTranslationKeys } from '../assessments/i18n';
import './assessment.scss';

export default function PixDataEntryPage(): JSX.Element {
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [completedSurvey, setCompletedSurvey] = useState<Survey.Model | null>(null);
  const isDirtyAssessment = (): boolean => !completedSurvey && isDirty;
  const { t } = useTranslation('translation', { keyPrefix: 'assessment' });
  const { facility } = useContext(ActiveFacilityContext);
  const contextFacilityFiltered = facility.value !== '' ? facility : undefined;
  const history = useHistory();
  const { user } = useContext(UserContext);
  const isAdmin = user?.role === Roles.ADMIN || user?.role === Roles.SUPER_ADMIN;

  const PIX_ASSESSMENT_NUMBER = 100039;

  const onCancelCallback = (): void => {
    history.push('/');
  };

  const onCompletingCallback = (_surveyInstance: any, options: any): void => {
    // eslint-disable-next-line no-alert, no-restricted-globals
    options.allowComplete = confirm(`You are submitting this assessment for ${contextFacilityFiltered?.label}.`);
  };

  const showAdminPopup = (survey: Survey.Model, options: any): Promise<void> => new Promise(() => {
    // eslint-disable-next-line no-alert
    alert('PIX submission is not available for admin users.');
    options.allowComplete = false;
  });

  const onAssessmentCompleteCallback = async (survey: Survey.Model, options: any): Promise<void> => {
    options.showDataSaving();
    setIsDirty(false);

    // Set facID to selected facility
    const surveyAnswers = { ...survey.data, FACID: contextFacilityFiltered?.value };

    try {
      const results = Object.entries(surveyAnswers);

      const promises: Promise<void>[] = [];

      // Construct the data format for the API.
      const resultsList: AssessmentResult[] = [];
      for (let i = 0; i < results.length; i += 1) {
        if (results[i][1] !== null && results[i][1] !== undefined) {
          const resultRow = {
            patientID: -1,
            assessmentNumber: PIX_ASSESSMENT_NUMBER,
            variableName: results[i][0],
            dataValue: String(results[i][1]),
          };
          resultsList.push(resultRow);
        }
      }

      promises.push(AssessmentProxy.saveAssessmentForm(resultsList));

      // Resolve promises
      Promise.all(promises)
        .then(() => {
          options.showDataSavingSuccess();
          setTimeout(() => {
            setCompletedSurvey(survey);
          }, 1000);
        })
        .catch((error: any) => {
          // Slight delay to show loading message briefly
          setTimeout(() => {
            options.showDataSavingError();
          }, 500);

          throw error;
        });
    } catch (error: any) {
      // Slight delay to show loading message briefly
      setTimeout(() => {
        options.showDataSavingError();
      }, 500);

      throw error;
    }
  };

  if (!hasPixAccess(contextFacilityFiltered?.pixDataEntry)) {
    return (
      <PageLayout layout={PageLayoutVariant.PADDED} testText="Assessment Form Page">
        <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="assessment-form-continue-container">
          <InlineText text="PIX features are not available for the selected facility." fontColor={FontColors.PRIMARY} fontSize={FontSizes.EXTRA_LARGE} />
        </FlexContainer>
      </PageLayout>
    );
  }

  if (completedSurvey) {
    return (
      <PageLayout layout={PageLayoutVariant.PADDED} testText="Assessment Form Page">
        <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="assessment-form-continue-container">
          <InlineText text="Submission successful." fontColor={FontColors.PRIMARY} fontSize={FontSizes.SUPER_EXTRA_LARGE} />
          <Button
            variant={ButtonVariant.PRIMARY}
            onClick={() => {
              setCompletedSurvey(null);
            }}
            extraClasses="restart-button"
          >
            <InlineText text="Start new form" fontColor={FontColors.BACKGROUND} fontSize={FontSizes.REGULAR} bold />
          </Button>
        </FlexContainer>
      </PageLayout>
    );
  }

  return (
    <>
      <AssessmentSurvey
        assessmentNumber={PIX_ASSESSMENT_NUMBER}
        instrumentTypeID={InstrumentTypes.PIX}
        appliedWhenID={AppliedWhen.PIX}
        patientAssessmentData={{
          accountID: -1,
          patientID: -1,
          programCode: -1,
          patientNumber: '-1',
          careID: -1,
          treatmentTypeID: -1,
          corporationID: -1,
          populationTypeID: -1,
          facilityID: -1,
        }}
        onCompleteCallback={onAssessmentCompleteCallback}
        setIsDirtyCallback={setIsDirty}
        onCancelCallback={onCancelCallback}
        isPix
        addClearButton
        onCompletingCallback={isAdmin ? showAdminPopup : onCompletingCallback}
      />
      <Prompt when={isDirtyAssessment()} message={t(assessmentTranslationKeys.dirtyPrompt)} />
    </>
  );
}
